// @ts-strict-ignore
import * as React from 'react';
import Icon, { CloseCircleFilled } from '@ant-design/icons';
import { Event } from '../../utils/analytics';
import { RootState } from '../../reducers/reducers';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    selectAllAppNamesAndPlatformsByAppId,
    selectAllAppsIdsInOrg,
    selectOrg,
    selectUser,
    selectIsWorkflowsCIStepEnabled,
    selectAllAppGroupNamesInOrg,
    selectApp,
    selectOrgGroups,
    selectAIDisabledForOrgInsights,
} from '../../selectors/selectors';
import { selectDestinationsData, selectCanAccessTabularView } from '../../selectors/summaryViewSelectors';
import { getDefaultVersionsForDestinations, getValidVersionForDestinations } from '../../helpers/summaryViewHelpers';
import { OrgComponentProps } from '../../typescriptHelpers';
import { SendOutlined } from '@ant-design/icons';
import { antGrey, blurryBackdropStyle, pageContentStyle, runwayColors, titleStyle } from '../styles/globalStyles';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import {
    AllReleaseTypes,
    OrgOverviewBucketSize,
    OrgOverviewReleaseTypesFilter,
    OrgOverviewAvailableMetric,
    OrderedStatisticCategory,
    OrgOverviewStatistic,
    ReleaseType,
    StatisticCategory,
    StatisticScope,
    StatisticType,
    SubscriptionFeatureType,
} from '../../types';
import { PageViewType } from '@/types/summaryView';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Typography from 'antd/lib/typography';
import {
    OrgOverviewEntityIds,
    OrgOverviewEntityRequest,
    OrgOverviewFiltersV2,
    tryFetchEntity,
} from '../../actions/entities';
import { FetchableEntityType } from '../../entityType';
import { useIsLoading } from '@/utils/hooks/helpers';
import { SummaryTable } from '../orgSummary/SummaryTable';
import { useSummaryView } from '@/utils/hooks/useSummaryView';
import { OverviewTableHeader, PageViewContext } from '../orgSummary/OverviewTableHeader';
import { createFilterApplyTracker } from '@/helpers/summaryViewHelpers';
import { trackOrgRouteEvent } from '../../actions/trackEvent';
import { flatten, isEqual } from 'lodash';
import { OrgOverviewAppMetricsTabBar, StatisticCategoryOrAll } from './OrgOverviewAppGroupsTabBar';
import {
    colorForStatCategory,
    displayTitleForStatCategory,
    iconForStatCategory,
    DEFAULT_ORG_OVERVIEW_BUCKET_SIZE,
} from './orgOverviewHelpers';
import {
    OrgOverviewDurationsBarGraphStat,
    OrgOverviewStatCard,
    OrgOverviewStatEmptyCard,
    OrgOverviewStatLoadingCard,
} from './OrgOverviewStatCards';
import { OrgOverviewReleaseStepDurationsV2 } from './OrgOverviewReleaseStepDurationsV2';
import { OrgOverviewInsightsAlert } from './OrgOverviewInsightsAlert';
import { EmptyStateWrapper, featureSectionForStatisticCategory } from '../releaseEmptyState/EmptyStateWrapper';
import {
    useRestrictedFeatureIsDisabled,
    useFeaturePlanRestrictionTooltipContent,
} from '../planRestrictions/planRestrictionHelperComponents';
import { useOrgNavigationFilters } from '../sidebar/OrgOverview/OrgNavigationFiltersContext';
import {
    useGetOrgAvailableMetricsQuery,
    useGetOrgBuildDistroBucketsQuery,
    useLazyGetOrgInsightsQuery,
    useLazyGetOrgOverviewQuery,
} from '@/middlewares/defaultApi';
import { Link } from 'react-router-dom';
import { UserSettingsPageView } from '../userSettings/UserSettingsComponent';
import { createUserSettingsRoute } from '@/helpers/routeHelpers';
import Layout, { Content } from 'antd/lib/layout/layout';
import Spin from 'antd/lib/spin';
import {
    clearStoredLayouts,
    GRID_DEFAULT_WIDTH,
    GRID_MIN_HEIGHT,
    GRID_MIN_WIDTH,
    OrgOverviewGridSection,
    OrgOverviewGridWidget,
} from './OrgOverviewGridSection';

import { AppAndOrgAvatar } from '../buildDistribution/CommonComponents';
import { TEST_ORG_IDS } from '@/constants';

const MIN_STAT_CARD_WIDTH = 288;
const ORG_OVERVIEW_FETCH_DEBOUNCE_MS = 500;
const ORG_OVERVIEW_METRIC_SCROLL_MARGIN_TOP_PX = 120;

interface StatCategorySectionProps {
    orgId: string;
    title?: string;
    isLoading: boolean;
    shouldShowPaywall: boolean;
    shouldForceEmptyState: boolean;
    stats: OrgOverviewStatistic[];
    category: StatisticCategory;
    scope: StatisticScope;
    isWorkflowsCIStepEnabled: boolean;
    highlightedMetricId: string | null;
    onMetricHighlightEnd: () => void;
    layoutResetVersion: number;
    customBucketSizes: Partial<Record<StatisticType, OrgOverviewBucketSize>>;
    bucketNamesById: Record<string, string>;
    supportsCustomBucketsByMetricType: Partial<Record<StatisticType, boolean>>;
    onBucketSizeChange: (metricType: StatisticType, bucketSize: OrgOverviewBucketSize) => void;
    loadingMetricTypes: ReadonlySet<StatisticType>;
}

interface OrgOverviewChartsContentProps {
    orgId: string;
    selectedStatCategory: StatisticCategoryOrAll;
    availableStatCategories: StatisticCategory[];
    onStatCategorySelected: (statCategory: StatisticCategoryOrAll) => void;
    categoriesToRender: StatisticCategory[];
    statisticsByCategory: Record<StatisticCategory, OrgOverviewStatistic[]>;
    shouldShowAllCategories: boolean;
    isOrgOverviewLoading: boolean;
    shouldShowPaywall: boolean;
    hasNoMetricsAcrossAllCategories: boolean;
    scope: StatisticScope;
    isWorkflowsCIStepEnabled: boolean;
    highlightedMetricId: string | null;
    onMetricHighlightEnd: () => void;
    onResetLayout: () => void;
    layoutResetVersion: number;
    customBucketSizes: Partial<Record<StatisticType, OrgOverviewBucketSize>>;
    bucketNamesById: Record<string, string>;
    supportsCustomBucketsByMetricType: Partial<Record<StatisticType, boolean>>;
    onBucketSizeChange: (metricType: StatisticType, bucketSize: OrgOverviewBucketSize) => void;
    loadingMetricTypes: ReadonlySet<StatisticType>;
}

function defaultGridWidthForStatisticType(type: StatisticType): number {
    switch (type) {
        case StatisticType.StatReleaseDurations:
            return 4;
        default:
            return GRID_DEFAULT_WIDTH;
    }
}

const StatCategorySection: React.FC<StatCategorySectionProps> = ({
    orgId,
    isLoading,
    shouldShowPaywall,
    shouldForceEmptyState,
    stats,
    title,
    scope,
    category,
    isWorkflowsCIStepEnabled,
    highlightedMetricId,
    onMetricHighlightEnd,
    layoutResetVersion,
    customBucketSizes,
    bucketNamesById,
    supportsCustomBucketsByMetricType,
    onBucketSizeChange,
    loadingMetricTypes,
}) => {
    const appNamesAndPlatformsByAppId = useSelector(selectAllAppNamesAndPlatformsByAppId);
    const paywallTooltipContent = useFeaturePlanRestrictionTooltipContent(SubscriptionFeatureType.OrgLevelMetrics);
    const isEmpty = !isLoading && stats.length === 0;
    const titleIcon = iconForStatCategory(category);
    const loadingGridWidgets = React.useMemo<OrgOverviewGridWidget[]>(
        () =>
            Array(4)
                .fill(0)
                .map((_, i) => ({
                    id: `loading-stat-${category}-${i}`,
                    w: GRID_DEFAULT_WIDTH,
                    h: GRID_MIN_HEIGHT,
                    minW: GRID_MIN_WIDTH,
                    minH: GRID_MIN_HEIGHT,
                    content: <OrgOverviewStatLoadingCard key={i} />,
                })),
        [category],
    );
    const gridWidgets = React.useMemo<OrgOverviewGridWidget[]>(() => {
        if (isLoading || isEmpty) {
            return [];
        }

        return stats.map((stat, i) => {
            const widgetId = `stat-${stat.type}`;
            const bucketSize = customBucketSizes[stat.type] ?? DEFAULT_ORG_OVERVIEW_BUCKET_SIZE;
            const supportsCustomBuckets = !!supportsCustomBucketsByMetricType[stat.type];
            const wrapWidgetContent = (content: React.ReactNode, options?: { width?: number }) => ({
                id: widgetId,
                w: options?.width ?? defaultGridWidthForStatisticType(stat.type),
                h: GRID_MIN_HEIGHT,
                minW: GRID_MIN_WIDTH,
                minH: GRID_MIN_HEIGHT,
                content: (
                    <div
                        id={stat.type}
                        className={highlightedMetricId === stat.type ? 'org-overview-metric-highlight' : undefined}
                        onAnimationEnd={highlightedMetricId === stat.type ? onMetricHighlightEnd : undefined}
                        style={{
                            height: '100%',
                            borderRadius: 8,
                            scrollMarginTop: ORG_OVERVIEW_METRIC_SCROLL_MARGIN_TOP_PX,
                        }}
                    >
                        {content}
                    </div>
                ),
            });

            switch (stat.type) {
                case StatisticType.StatAverageReleaseStepCompletionTime:
                case StatisticType.AverageHotfixStepCompletionTime:
                    return wrapWidgetContent(
                        <OrgOverviewReleaseStepDurationsV2
                            key={i}
                            {...stat}
                            scope={scope}
                            isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                        />,
                    );

                case StatisticType.StatReleaseDurations:
                    return wrapWidgetContent(
                        <OrgOverviewDurationsBarGraphStat
                            key={i}
                            {...stat}
                            scope={scope}
                            useNewChartsLibrary
                            bucketSize={bucketSize}
                            supportsCustomBuckets={supportsCustomBuckets}
                            onBucketSizeChange={(nextBucketSize) => onBucketSizeChange(stat.type, nextBucketSize)}
                            isLoading={loadingMetricTypes.has(stat.type)}
                        />,
                    );
                default:
                    return wrapWidgetContent(
                        <OrgOverviewStatCard
                            key={i}
                            orgId={orgId}
                            {...stat}
                            scope={scope}
                            appNamesAndPlatformsByAppId={appNamesAndPlatformsByAppId}
                            bucketNamesById={bucketNamesById}
                            useNewChartsLibrary
                            bucketSize={bucketSize}
                            supportsCustomBuckets={supportsCustomBuckets}
                            onBucketSizeChange={(nextBucketSize) => onBucketSizeChange(stat.type, nextBucketSize)}
                            isLoading={loadingMetricTypes.has(stat.type)}
                        />,
                    );
            }
        });
    }, [
        appNamesAndPlatformsByAppId,
        highlightedMetricId,
        isEmpty,
        isLoading,
        isWorkflowsCIStepEnabled,
        customBucketSizes,
        bucketNamesById,
        loadingMetricTypes,
        onBucketSizeChange,
        scope,
        stats,
        supportsCustomBucketsByMetricType,
    ]);

    return (
        <Space direction="vertical" style={{ width: '100%', marginTop: 30 }} size={20}>
            <div
                style={{
                    display: 'flex',
                    gap: 20,
                    alignItems: 'center',
                }}
            >
                {title && (
                    <>
                        <Typography.Text
                            style={{
                                fontSize: 20,
                                color: antGrey.grey7,
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            {titleIcon && (
                                <Icon
                                    component={titleIcon}
                                    style={{
                                        color: colorForStatCategory(category),
                                    }}
                                />
                            )}
                            {title}
                        </Typography.Text>
                        <div
                            style={{
                                flex: 1,
                                height: '100%',
                            }}
                        >
                            <Divider
                                type="horizontal"
                                style={{
                                    width: '100%',
                                    margin: 0,
                                }}
                            />
                        </div>
                    </>
                )}
            </div>

            <EmptyStateWrapper
                featureSections={[featureSectionForStatisticCategory(category)]}
                shouldCenterContent
                shouldForceEnable={isEmpty && shouldForceEmptyState}
                paywallContent={
                    shouldShowPaywall ?
                        <div className="org-overview-paywall-content">{paywallTooltipContent}</div>
                    :   undefined
                }
                appId={null}
                releaseId={null}
                extraInfo={{
                    scope,
                }}
            >
                {!isLoading && !isEmpty ?
                    <div style={{ width: '100%' }}>
                        <OrgOverviewGridSection
                            key={`${category}-${scope}`}
                            widgets={gridWidgets}
                            storageKey={`${category}-${scope}`}
                            resetVersion={layoutResetVersion}
                        />
                    </div>
                : isLoading ?
                    <div style={{ width: '100%' }}>
                        <OrgOverviewGridSection widgets={loadingGridWidgets} resetVersion={layoutResetVersion} />
                    </div>
                :   <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_STAT_CARD_WIDTH}px, 1fr))`,
                            gap: 20,
                            flex: 1,
                            gridAutoFlow: 'row',
                        }}
                    >
                        {Array(6)
                            .fill(0)
                            .map((_, i) => (
                                <OrgOverviewStatEmptyCard key={i} />
                            ))}
                    </div>
                }
            </EmptyStateWrapper>
        </Space>
    );
};

const OrgOverviewChartsContent: React.FC<OrgOverviewChartsContentProps> = React.memo(
    ({
        orgId,
        selectedStatCategory,
        availableStatCategories,
        onStatCategorySelected,
        categoriesToRender,
        statisticsByCategory,
        shouldShowAllCategories,
        isOrgOverviewLoading,
        shouldShowPaywall,
        hasNoMetricsAcrossAllCategories,
        scope,
        isWorkflowsCIStepEnabled,
        highlightedMetricId,
        onMetricHighlightEnd,
        onResetLayout,
        layoutResetVersion,
        customBucketSizes,
        bucketNamesById,
        supportsCustomBucketsByMetricType,
        onBucketSizeChange,
        loadingMetricTypes,
    }) => (
        <>
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    paddingTop: 32,
                    zIndex: 2,
                    width: '100%',
                    ...blurryBackdropStyle,
                }}
            >
                <OrgOverviewAppMetricsTabBar
                    selectedStatCategory={selectedStatCategory}
                    availableStatCategories={availableStatCategories}
                    onStatCategorySelected={onStatCategorySelected}
                    onResetLayout={onResetLayout}
                />
            </div>

            <div
                style={{
                    position: 'relative',
                    marginBottom: 56,
                }}
            >
                {categoriesToRender.map((category) => {
                    const stats: OrgOverviewStatistic[] = statisticsByCategory[category] ?? [];
                    return (
                        <StatCategorySection
                            key={category}
                            orgId={orgId}
                            category={category}
                            title={shouldShowAllCategories ? displayTitleForStatCategory(category) : null}
                            isLoading={isOrgOverviewLoading}
                            shouldShowPaywall={shouldShowPaywall}
                            shouldForceEmptyState={hasNoMetricsAcrossAllCategories}
                            stats={stats}
                            scope={scope}
                            isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                            highlightedMetricId={highlightedMetricId}
                            onMetricHighlightEnd={onMetricHighlightEnd}
                            layoutResetVersion={layoutResetVersion}
                            customBucketSizes={customBucketSizes}
                            bucketNamesById={bucketNamesById}
                            supportsCustomBucketsByMetricType={supportsCustomBucketsByMetricType}
                            onBucketSizeChange={onBucketSizeChange}
                            loadingMetricTypes={loadingMetricTypes}
                        />
                    );
                })}
            </div>
        </>
    ),
);

const OrgOverviewV2: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const dispatch = useDispatch();

    const [trackedPageView, setTrackedPageView] = React.useState<string | null>(null);
    const handlePageViewTracking = React.useCallback((pageView: string) => {
        setTrackedPageView((prev) => (prev === pageView ? prev : pageView));
    }, []);

    const [selectedStatCategory, setSelectedStatCategory] = React.useState<StatisticCategoryOrAll>('all');
    const {
        filters: navigationFilters,
        setFilters: setNavigationFilters,
        setShouldSyncUrlFilters,
    } = useOrgNavigationFilters();

    React.useEffect(() => {
        setShouldSyncUrlFilters(true);

        return () => {
            setShouldSyncUrlFilters(false);
        };
    }, [setShouldSyncUrlFilters]);

    const { data: availableMetricsResponse } = useGetOrgAvailableMetricsQuery({ orgId: props.orgId });
    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, props.orgId) || {});
    const { selectedUserIds, selectedUserGroupIds } = React.useMemo(() => {
        return Object.keys(navigationFilters.selectedUsers).reduce(
            (acc, selectedId) => {
                if (orgGroupsById[selectedId]) {
                    acc.selectedUserGroupIds.push(selectedId);
                } else {
                    acc.selectedUserIds.push(selectedId);
                }

                return acc;
            },
            { selectedUserIds: [] as string[], selectedUserGroupIds: [] as string[] },
        );
    }, [navigationFilters.selectedUsers, orgGroupsById]);
    const selectedReleaseTypes = React.useMemo<ReleaseType[]>(() => {
        switch (navigationFilters.selectedReleaseTypes) {
            case OrgOverviewReleaseTypesFilter.AllExceptHotfix:
                return AllReleaseTypes.filter((releaseType) => releaseType !== ReleaseType.Hotfix);
            case OrgOverviewReleaseTypesFilter.HotfixOnly:
                return [ReleaseType.Hotfix];
            default:
                return AllReleaseTypes;
        }
    }, [navigationFilters.selectedReleaseTypes]);
    const availableMetricsByType = React.useMemo(
        () =>
            (availableMetricsResponse?.data?.metrics ?? []).reduce(
                (acc, metric) => {
                    acc[metric.type] = metric;
                    return acc;
                },
                {} as Partial<Record<StatisticType, OrgOverviewAvailableMetric>>,
            ),
        [availableMetricsResponse],
    );
    const supportsCustomBucketsByMetricType = React.useMemo(
        () =>
            Object.entries(availableMetricsByType).reduce(
                (acc, [metricType, metric]) => {
                    acc[metricType as StatisticType] = !!metric?.supportsCustomBuckets;
                    return acc;
                },
                {} as Partial<Record<StatisticType, boolean>>,
            ),
        [availableMetricsByType],
    );
    const selectedCustomBucketSizes = React.useMemo(
        () =>
            (navigationFilters.selectedMetrics ?? []).reduce(
                (acc, metricType) => {
                    acc[metricType as StatisticType] =
                        navigationFilters.customBucketSizes?.[metricType] ?? DEFAULT_ORG_OVERVIEW_BUCKET_SIZE;
                    return acc;
                },
                {} as Partial<Record<StatisticType, OrgOverviewBucketSize>>,
            ),
        [navigationFilters.customBucketSizes, navigationFilters.selectedMetrics],
    );
    const orgDestinationsData = useSelector((state: RootState) => selectDestinationsData(state, props.orgId));
    const isLoadingOrgDestinations = useIsLoading(FetchableEntityType.OrgDestinationsAndVersions, props.orgId);
    const appsById = useSelector((state: RootState) => state.entities.apps.byId);
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));
    const shouldUseOrgTagsFilter = TEST_ORG_IDS.includes(props.orgId);
    const appIdsInOrg = useSelector((state: RootState) => selectAllAppsIdsInOrg(state, props.orgId) ?? []);
    const { data: orgBuildDistroBucketsResponse } = useGetOrgBuildDistroBucketsQuery({ orgId: props.orgId });
    const bucketIdsByAppId = React.useMemo(
        () =>
            (orgBuildDistroBucketsResponse?.data?.buckets ?? []).reduce(
                (acc, bucket) => {
                    if (!acc[bucket.appId]) {
                        acc[bucket.appId] = [];
                    }

                    acc[bucket.appId].push(bucket.id);
                    return acc;
                },
                {} as Record<string, string[]>,
            ),
        [orgBuildDistroBucketsResponse?.data?.buckets],
    );
    const bucketNamesById = React.useMemo(
        () =>
            (orgBuildDistroBucketsResponse?.data?.buckets ?? []).reduce(
                (acc, bucket) => {
                    acc[bucket.id] = bucket.name;
                    return acc;
                },
                {} as Record<string, string>,
            ),
        [orgBuildDistroBucketsResponse?.data?.buckets],
    );

    React.useEffect(() => {
        if (!orgDestinationsData && !isLoadingOrgDestinations) {
            dispatch(
                tryFetchEntity(FetchableEntityType.OrgDestinationsAndVersions, { orgId: props.orgId }, props.orgId),
            );
        }
    }, [dispatch, props.orgId, orgDestinationsData, isLoadingOrgDestinations]);

    const selectedAppToEntityIds = React.useMemo(() => {
        const selectedDestinations = navigationFilters.selectedDestinations;
        const selectedWorkflowApps = navigationFilters.selectedWorkflowApps;
        const selectedPlatforms = navigationFilters.selectedPlatforms;
        const selectedBuildBuckets = navigationFilters.selectedBuildBuckets;
        const selectedOrgTags = shouldUseOrgTagsFilter ? navigationFilters.selectedOrgTags : {};
        const hasSelectedDestinations = Object.keys(selectedDestinations).length > 0;
        const hasSelectedWorkflowApps = Object.keys(selectedWorkflowApps).length > 0;
        const hasSelectedPlatforms = Object.keys(selectedPlatforms).length > 0;
        const hasSelectedBuildBuckets = Object.keys(selectedBuildBuckets).length > 0;
        const hasSelectedOrgTags = Object.keys(selectedOrgTags).length > 0;
        const selectedTagDestinationIds = (org?.tags ?? []).reduce(
            (acc, tag) => {
                if (!selectedOrgTags[tag.id]) {
                    return acc;
                }

                (tag.destinations ?? []).forEach((destinationId) => {
                    acc[destinationId] = destinationId;
                });
                return acc;
            },
            {} as Record<string, string>,
        );
        const destinationIdsByAppId = (orgDestinationsData?.appDestinations ?? []).reduce(
            (acc, { appId, destinations }) => {
                acc[appId] = destinations.reduce((innerAcc, destination) => {
                    if (destination.destinationId) {
                        innerAcc.push(destination.destinationId);
                    }

                    return innerAcc;
                }, [] as string[]);
                return acc;
            },
            {} as Record<string, string[]>,
        );

        return appIdsInOrg.reduce(
            (acc, appId) => {
                const app = appsById[appId];
                if (!app?.appSettings) {
                    return acc;
                }

                const destinationIds = destinationIdsByAppId[appId] ?? [];
                const bucketIds = bucketIdsByAppId[appId] ?? [];

                if (
                    !hasSelectedDestinations &&
                    !hasSelectedWorkflowApps &&
                    !hasSelectedPlatforms &&
                    !hasSelectedBuildBuckets &&
                    !hasSelectedOrgTags
                ) {
                    acc[appId] = { destinationIds, bucketIds };
                    return acc;
                }

                const matchesSelectedWorkflowApps = !hasSelectedWorkflowApps || !!selectedWorkflowApps[appId];
                const matchesSelectedPlatforms = !hasSelectedPlatforms || !!selectedPlatforms[app.appSettings.platform];
                const selectedDestinationIdsForApp = destinationIds.filter(
                    (destinationId) => !!selectedDestinations[destinationId],
                );
                const selectedTagDestinationIdsForApp = destinationIds.filter(
                    (destinationId) => !!selectedTagDestinationIds[destinationId],
                );
                const hasLegacyAppSelection = !!selectedDestinations[appId];
                const selectedBucketIdsForApp = bucketIds.filter((bucketId) => !!selectedBuildBuckets[bucketId]);
                const shouldIncludeAppFromWorkflowOrPlatformFilters =
                    (hasSelectedWorkflowApps && matchesSelectedWorkflowApps) ||
                    (hasSelectedPlatforms && matchesSelectedPlatforms);

                if (hasSelectedDestinations && (selectedDestinationIdsForApp.length || hasLegacyAppSelection)) {
                    acc[appId] = {
                        ...acc[appId],
                        destinationIds: selectedDestinationIdsForApp,
                    };
                }

                if (hasSelectedOrgTags && selectedTagDestinationIdsForApp.length) {
                    acc[appId] = {
                        ...acc[appId],
                        destinationIds: Array.from(
                            new Set([...(acc[appId]?.destinationIds ?? []), ...selectedTagDestinationIdsForApp]),
                        ),
                    };
                }

                if (hasSelectedBuildBuckets && selectedBucketIdsForApp.length) {
                    acc[appId] = {
                        ...acc[appId],
                        bucketIds: selectedBucketIdsForApp,
                    };
                }

                if (shouldIncludeAppFromWorkflowOrPlatformFilters) {
                    acc[appId] = { destinationIds, bucketIds };
                }

                return acc;
            },
            {} as Record<string, OrgOverviewEntityIds>,
        );
    }, [
        appIdsInOrg,
        appsById,
        bucketIdsByAppId,
        navigationFilters.selectedBuildBuckets,
        navigationFilters.selectedDestinations,
        navigationFilters.selectedOrgTags,
        navigationFilters.selectedPlatforms,
        navigationFilters.selectedWorkflowApps,
        orgDestinationsData?.appDestinations,
        org?.tags,
        shouldUseOrgTagsFilter,
    ]);

    const destinationIdsByAppId = React.useMemo(
        () =>
            (orgDestinationsData?.appDestinations ?? []).reduce(
                (acc, { appId, destinations }) => {
                    acc[appId] = (destinations ?? []).reduce((ids, destination) => {
                        if (destination.destinationId) {
                            ids.push(destination.destinationId);
                        }
                        return ids;
                    }, [] as string[]);
                    return acc;
                },
                {} as Record<string, string[]>,
            ),
        [orgDestinationsData?.appDestinations],
    );

    const selectedStatFilters = React.useMemo<OrgOverviewFiltersV2>(
        () => ({
            orgId: props.orgId,
            appToEntityIds: selectedAppToEntityIds,
            tagIds: shouldUseOrgTagsFilter ? Object.keys(navigationFilters.selectedOrgTags) : [],
            userIds: selectedUserIds,
            userGroupIds: selectedUserGroupIds,
            selectedMetrics: navigationFilters.selectedMetrics as StatisticType[],
            startDate: navigationFilters.selectedDateRange.start,
            endDate: navigationFilters.selectedDateRange.end,
            releaseTypes: selectedReleaseTypes,
            customBucketSizes: selectedCustomBucketSizes,
        }),
        [
            props.orgId,
            navigationFilters.selectedDateRange.end,
            navigationFilters.selectedDateRange.start,
            navigationFilters.selectedMetrics,
            navigationFilters.selectedOrgTags,
            selectedAppToEntityIds,
            selectedReleaseTypes,
            selectedCustomBucketSizes,
            selectedUserGroupIds,
            selectedUserIds,
            shouldUseOrgTagsFilter,
        ],
    );

    const statAppIds = Object.keys(selectedAppToEntityIds ?? {});
    const selectedStatFiltersKey = React.useMemo(
        () =>
            JSON.stringify({
                filters: selectedStatFilters,
                appIds: statAppIds.slice().sort(),
            }),
        [selectedStatFilters, statAppIds],
    );

    const lastSubmittedFiltersRef = React.useRef<OrgOverviewFiltersV2 | null>(null);
    const [fetchOrgOverview, { data: orgOverviewResponse, isLoading, isFetching }] = useLazyGetOrgOverviewQuery();
    const [fetchSingleMetricOverview] = useLazyGetOrgOverviewQuery();
    const lastOrgOverviewRequestRef = React.useRef<ReturnType<typeof fetchOrgOverview> | null>(null);
    const orgOverviewFetchTimeoutRef = React.useRef<number | null>(null);
    const [isDebouncingOrgOverviewFetch, setIsDebouncingOrgOverviewFetch] = React.useState(false);
    const [loadingMetricTypes, setLoadingMetricTypes] = React.useState<Set<StatisticType>>(new Set());
    const [statisticsOverridesByType, setStatisticsOverridesByType] = React.useState<
        Record<string, OrgOverviewStatistic[]>
    >({});
    const currentFiltersVersionRef = React.useRef(0);
    const latestMetricRequestIdRef = React.useRef<Partial<Record<StatisticType, number>>>({});
    const metricRequestSequenceRef = React.useRef(0);

    const isAIDisabled = useSelector((state: RootState) => selectAIDisabledForOrgInsights(state, props.orgId));
    const shouldShowPaywall = useRestrictedFeatureIsDisabled(SubscriptionFeatureType.OrgLevelMetrics);
    const [fetchOrgInsights, { data: orgInsightsResponse, isLoading: isLoadingInsights }] =
        useLazyGetOrgInsightsQuery();
    const fetchedInsightsOrgIdsRef = React.useRef(new Set<string>());

    const { orgId, orgName, orgImageUrl } = useSelector((state: RootState) => {
        const org = selectOrg(state, props.orgId);
        return {
            orgId: org.orgId,
            orgName: org.name,
            orgImageUrl: org.orgImageUrl,
        };
    }, shallowEqual);

    const canAccessTabularView = useSelector((state: RootState) => selectCanAccessTabularView(state, props.orgId));

    React.useEffect(() => {
        if (!canAccessTabularView) {
            handlePageViewTracking(PageViewType.Default);
        }
    }, [canAccessTabularView, handlePageViewTracking]);

    const selectedStatFilterAppIds = Object.keys(selectedStatFilters?.appToEntityIds ?? {});
    const hasSelectedMetrics = (selectedStatFilters?.selectedMetrics?.length ?? 0) > 0;
    const singleAppId = selectedStatFilterAppIds.length === 1 ? selectedStatFilterAppIds[0] : null;
    const singleAppSelected = useSelector((state: RootState) => (singleAppId ? selectApp(state, singleAppId) : null));
    const orgOverview = orgOverviewResponse?.data;

    const isWaitingForInitialOrgOverviewFilters =
        hasSelectedMetrics && !selectedStatFilterAppIds.length && !lastSubmittedFiltersRef.current;
    const isOrgOverviewLoading =
        isLoading || isFetching || isWaitingForInitialOrgOverviewFilters || isDebouncingOrgOverviewFetch;
    const overviewImageUrl = orgImageUrl;
    const overviewTitle = orgName;
    const selectedHeaderAppName = singleAppSelected?.appSettings.appName;
    const selectedHeaderAppImageUrl = singleAppSelected?.appSettings.appImageUrl;

    const clearSelectedHeaderApp = React.useCallback(() => {
        if (!singleAppId) {
            return;
        }

        setNavigationFilters((currentFilters) => {
            const nextWorkflowApps = { ...currentFilters.selectedWorkflowApps };
            const nextDestinations = { ...currentFilters.selectedDestinations };
            const nextBuildBuckets = { ...currentFilters.selectedBuildBuckets };

            delete nextWorkflowApps[singleAppId];
            delete nextDestinations[singleAppId];
            (destinationIdsByAppId[singleAppId] ?? []).forEach((destinationId) => {
                delete nextDestinations[destinationId];
            });
            (bucketIdsByAppId[singleAppId] ?? []).forEach((bucketId) => {
                delete nextBuildBuckets[bucketId];
            });

            return {
                ...currentFilters,
                selectedWorkflowApps: nextWorkflowApps,
                selectedDestinations: nextDestinations,
                selectedBuildBuckets: nextBuildBuckets,
            };
        });
    }, [bucketIdsByAppId, destinationIdsByAppId, singleAppId, setNavigationFilters]);

    React.useEffect(() => {
        if (!trackedPageView) return;
        dispatch(
            trackOrgRouteEvent({
                orgId: props.orgId,
                eventName: Event.ViewOrgOverview,
                viewType: trackedPageView,
            }),
        );
    }, [dispatch, props.orgId, trackedPageView]);

    React.useEffect(() => {
        currentFiltersVersionRef.current += 1;
    }, [selectedStatFiltersKey]);

    React.useEffect(() => {
        if (
            !navigationFilters.shouldGenerateInsights ||
            fetchedInsightsOrgIdsRef.current.has(props.orgId) ||
            isAIDisabled ||
            shouldShowPaywall
        ) {
            return;
        }

        fetchedInsightsOrgIdsRef.current.add(props.orgId);
        void fetchOrgInsights({ orgId: props.orgId }, true);
    }, [fetchOrgInsights, navigationFilters.shouldGenerateInsights, props.orgId, isAIDisabled, shouldShowPaywall]);

    const scope: StatisticScope = StatisticScope.Org;

    const statAppIdsKey = React.useMemo(() => statAppIds.slice().sort().join(','), [statAppIds]);

    React.useEffect(() => {
        return () => {
            if (orgOverviewFetchTimeoutRef.current) {
                window.clearTimeout(orgOverviewFetchTimeoutRef.current);
                orgOverviewFetchTimeoutRef.current = null;
            }
        };
    }, []);

    React.useEffect(() => {
        const clearPendingFetch = () => {
            if (orgOverviewFetchTimeoutRef.current) {
                window.clearTimeout(orgOverviewFetchTimeoutRef.current);
                orgOverviewFetchTimeoutRef.current = null;
            }
            setIsDebouncingOrgOverviewFetch(false);
        };

        if (shouldShowPaywall) {
            return clearPendingFetch;
        }

        // If the only filter change is a bucket-size update, skip the normal full overview refetch.
        // Bucket-size changes are handled separately by fetching and updating just the affected chart.
        const didOnlyCustomBucketSizesChange =
            !!lastSubmittedFiltersRef.current &&
            isEqual(
                { ...lastSubmittedFiltersRef.current, customBucketSizes: undefined },
                { ...selectedStatFilters, customBucketSizes: undefined },
            ) &&
            !isEqual(
                lastSubmittedFiltersRef.current?.customBucketSizes ?? {},
                selectedStatFilters.customBucketSizes ?? {},
            );

        if (lastSubmittedFiltersRef.current && isEqual(lastSubmittedFiltersRef.current, selectedStatFilters)) {
            return clearPendingFetch;
        }

        if (didOnlyCustomBucketSizesChange) {
            return clearPendingFetch;
        }

        if (!selectedStatFilterAppIds.length) {
            return clearPendingFetch;
        }

        if (!selectedStatFilters.selectedMetrics?.length) {
            clearPendingFetch();
            lastOrgOverviewRequestRef.current?.abort();
            return clearPendingFetch;
        }

        const request: OrgOverviewEntityRequest<OrgOverviewFiltersV2> = {
            orgId: props.orgId,
            filters: selectedStatFilters,
            appIds: statAppIds,
        };

        if (!request.appIds.length) {
            return clearPendingFetch;
        }

        clearPendingFetch();
        setIsDebouncingOrgOverviewFetch(true);

        orgOverviewFetchTimeoutRef.current = window.setTimeout(() => {
            lastOrgOverviewRequestRef.current?.abort();
            lastSubmittedFiltersRef.current = selectedStatFilters;
            lastOrgOverviewRequestRef.current = fetchOrgOverview(request, true);
            orgOverviewFetchTimeoutRef.current = null;
            setIsDebouncingOrgOverviewFetch(false);
        }, ORG_OVERVIEW_FETCH_DEBOUNCE_MS);

        return clearPendingFetch;
    }, [
        selectedStatFilterAppIds.length,
        selectedStatFilters,
        fetchOrgOverview,
        props.orgId,
        statAppIds,
        statAppIdsKey,
        shouldShowPaywall,
    ]);

    React.useEffect(() => {
        setStatisticsOverridesByType({});
        setLoadingMetricTypes(new Set());
    }, [orgOverview]);

    const statisticsByType = React.useMemo(() => {
        const nextStatisticsByType = (orgOverview?.statistics ?? []).reduce(
            (acc, stat) => {
                acc[stat.type] = [...(acc[stat.type] ?? []), stat];
                return acc;
            },
            {} as Record<string, OrgOverviewStatistic[]>,
        );

        return Object.entries(statisticsOverridesByType).reduce((acc, [metricType, stats]) => {
            if (stats.length > 0) {
                acc[metricType] = stats;
            } else {
                delete acc[metricType];
            }
            return acc;
        }, nextStatisticsByType);
    }, [orgOverview?.statistics, statisticsOverridesByType]);

    const allStatCategories = React.useMemo(
        () =>
            Object.entries(OrderedStatisticCategory)
                .sort((a, b) => a[1] - b[1])
                .map(([category]) => category as StatisticCategory),
        [],
    );

    const statistics = React.useMemo(() => flatten(Object.values(statisticsByType)), [statisticsByType]);
    const hasNoMetricsAcrossAllCategories = statistics.length === 0 && (!!orgOverview || !isOrgOverviewLoading);

    const availableStatCategories = React.useMemo(
        () =>
            isOrgOverviewLoading || hasNoMetricsAcrossAllCategories ? allStatCategories : (
                allStatCategories.filter((category) => statistics.some((statistic) => statistic.category === category))
            ),
        [allStatCategories, hasNoMetricsAcrossAllCategories, isOrgOverviewLoading, statistics],
    );

    const statisticsByCategory = React.useMemo(() => {
        const groupedStatistics = statistics
            .filter((statistic) => statistic.category === selectedStatCategory || selectedStatCategory === 'all')
            .reduce(
                (acc, statistic) => ({
                    ...acc,
                    [statistic.category]: [...(acc[statistic.category] ?? []), statistic],
                }),
                {} as Record<StatisticCategory, OrgOverviewStatistic[]>,
            );

        // Pin wider cards first so full-width widgets do not leave awkward gaps, then sort alphabetically.
        return Object.entries(groupedStatistics).reduce(
            (acc, [category, categoryStatistics]) => ({
                ...acc,
                [category]: [...categoryStatistics].sort((left, right) => {
                    const leftWidth = defaultGridWidthForStatisticType(left.type);
                    const rightWidth = defaultGridWidthForStatisticType(right.type);
                    if (leftWidth !== rightWidth) {
                        return rightWidth - leftWidth;
                    }

                    const leftLabel = left.displayText || left.type;
                    const rightLabel = right.displayText || right.type;
                    const titleComparison = leftLabel.localeCompare(rightLabel, undefined, { sensitivity: 'base' });

                    return titleComparison !== 0 ? titleComparison : left.type.localeCompare(right.type);
                }),
            }),
            {} as Record<StatisticCategory, OrgOverviewStatistic[]>,
        );
    }, [selectedStatCategory, statistics]);

    const shouldShowAllCategories = selectedStatCategory === 'all';

    const categoriesToRender = React.useMemo(
        () => (shouldShowAllCategories ? availableStatCategories : [selectedStatCategory as StatisticCategory]),
        [availableStatCategories, selectedStatCategory, shouldShowAllCategories],
    );

    React.useEffect(() => {
        if (selectedStatCategory === 'all') {
            return;
        }

        if (!availableStatCategories.includes(selectedStatCategory as StatisticCategory)) {
            setSelectedStatCategory('all');
        }
    }, [availableStatCategories, selectedStatCategory]);

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, singleAppId),
    );

    const insightsAlert = orgInsightsResponse?.data;
    const [highlightedMetricId, setHighlightedMetricId] = React.useState<string | null>(null);
    const [layoutResetVersion, setLayoutResetVersion] = React.useState(0);

    const handleStatCategorySelected = React.useCallback((statCategory: StatisticCategoryOrAll) => {
        setSelectedStatCategory(statCategory);
    }, []);

    const handleMetricScroll = React.useCallback((metricId: string) => {
        setHighlightedMetricId(metricId);
    }, []);

    const handleMetricHighlightEnd = React.useCallback(() => {
        setHighlightedMetricId(null);
    }, []);

    const handleResetLayout = React.useCallback(() => {
        clearStoredLayouts(categoriesToRender.map((category) => `${category}-${scope}`));
        setLayoutResetVersion((currentVersion) => currentVersion + 1);
    }, [categoriesToRender, scope]);

    const handleMetricBucketSizeChange = React.useCallback(
        (metricType: StatisticType, bucketSize: OrgOverviewBucketSize) => {
            if (shouldShowPaywall) {
                return;
            }

            const currentBucketSize = selectedCustomBucketSizes[metricType] ?? DEFAULT_ORG_OVERVIEW_BUCKET_SIZE;
            if (currentBucketSize === bucketSize || loadingMetricTypes.has(metricType)) {
                return;
            }

            if (!statAppIds.length) {
                return;
            }

            const requestFiltersVersion = currentFiltersVersionRef.current;
            const requestId = metricRequestSequenceRef.current + 1;
            metricRequestSequenceRef.current = requestId;
            latestMetricRequestIdRef.current[metricType] = requestId;

            setLoadingMetricTypes((current) => new Set(current).add(metricType));

            fetchSingleMetricOverview(
                {
                    orgId: props.orgId,
                    appIds: statAppIds,
                    filters: {
                        ...selectedStatFilters,
                        selectedMetrics: [metricType],
                        customBucketSizes: {
                            [metricType]: bucketSize,
                        },
                    },
                },
                true,
            )
                .unwrap()
                .then((response) => {
                    if (currentFiltersVersionRef.current !== requestFiltersVersion) {
                        return;
                    }

                    if (latestMetricRequestIdRef.current[metricType] !== requestId) {
                        return;
                    }

                    const nextStatistics = response?.data?.statistics ?? [];
                    setStatisticsOverridesByType((current) => {
                        const next = { ...current };
                        if (nextStatistics.length > 0) {
                            next[metricType] = nextStatistics;
                        } else {
                            delete next[metricType];
                        }
                        return next;
                    });
                    setNavigationFilters((currentFilters) => ({
                        ...currentFilters,
                        customBucketSizes: {
                            ...currentFilters.customBucketSizes,
                            [metricType]: bucketSize,
                        },
                    }));
                })
                .catch(() => {
                    return;
                })
                .finally(() => {
                    setLoadingMetricTypes((current) => {
                        const next = new Set(current);
                        next.delete(metricType);
                        return next;
                    });
                });
        },
        [
            fetchSingleMetricOverview,
            loadingMetricTypes,
            props.orgId,
            selectedStatFilters,
            selectedCustomBucketSizes,
            setNavigationFilters,
            statAppIds,
            shouldShowPaywall,
        ],
    );

    const renderChartsView = () => (
        <>
            {navigationFilters.shouldGenerateInsights && !isAIDisabled ?
                <OrgOverviewInsightsAlert
                    orgId={props.orgId}
                    availableMetrics={availableMetricsResponse?.data?.metrics ?? []}
                    insightsAlert={insightsAlert}
                    isLoading={isLoadingInsights}
                    onMetricScroll={handleMetricScroll}
                    onClose={() =>
                        setNavigationFilters((currentFilters) => ({
                            ...currentFilters,
                            shouldGenerateInsights: false,
                        }))
                    }
                />
            :   null}

            <OrgOverviewChartsContent
                orgId={props.orgId}
                selectedStatCategory={selectedStatCategory}
                availableStatCategories={availableStatCategories}
                onStatCategorySelected={handleStatCategorySelected}
                categoriesToRender={categoriesToRender}
                statisticsByCategory={statisticsByCategory}
                shouldShowAllCategories={shouldShowAllCategories}
                isOrgOverviewLoading={isOrgOverviewLoading}
                shouldShowPaywall={shouldShowPaywall}
                hasNoMetricsAcrossAllCategories={hasNoMetricsAcrossAllCategories}
                scope={scope}
                isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                highlightedMetricId={highlightedMetricId}
                onMetricHighlightEnd={handleMetricHighlightEnd}
                onResetLayout={handleResetLayout}
                layoutResetVersion={layoutResetVersion}
                customBucketSizes={selectedCustomBucketSizes}
                bucketNamesById={bucketNamesById}
                supportsCustomBucketsByMetricType={supportsCustomBucketsByMetricType}
                onBucketSizeChange={handleMetricBucketSizeChange}
                loadingMetricTypes={loadingMetricTypes}
            />
        </>
    );

    return (
        <Layout
            style={{
                ...pageContentStyle,
                paddingTop: 0,
                paddingBottom: 0,
            }}
        >
            <Content
                style={{
                    overflowY: 'scroll',
                    overflowX: 'hidden',
                    paddingLeft: 32,
                    paddingRight: 32,
                    position: 'relative',
                }}
            >
                <div
                    style={{
                        zIndex: 1,
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            paddingTop: 24,
                            alignItems: 'flex-start',
                            gap: 16,
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                            {singleAppSelected ?
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        border: `1px solid ${runwayColors.primary}`,
                                        backgroundColor: antGrey.grey2,
                                        borderRadius: 16,
                                        padding: 8,
                                        width: 800,
                                        minHeight: 72,
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                        <AppAndOrgAvatar url={selectedHeaderAppImageUrl} name={selectedHeaderAppName} />
                                        <div style={{ minWidth: 0 }}>
                                            <Text
                                                style={{
                                                    fontSize: 24,
                                                    fontWeight: 600,
                                                    color: antGrey.grey8,
                                                    display: 'block',
                                                }}
                                            >
                                                {selectedHeaderAppName}
                                            </Text>
                                            <Text
                                                style={{
                                                    ...titleStyle,
                                                    color: antGrey.grey6,
                                                    letterSpacing: '0.16em',
                                                }}
                                            >
                                                Overview
                                            </Text>
                                        </div>
                                    </div>
                                    <Button
                                        type="text"
                                        shape="circle"
                                        icon={<CloseCircleFilled style={{ color: antGrey.grey5, fontSize: 28 }} />}
                                        onClick={clearSelectedHeaderApp}
                                    />
                                </div>
                            :   <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <AppAndOrgAvatar url={overviewImageUrl} name={overviewTitle} />
                                    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                        <Text
                                            style={{
                                                fontSize: 24,
                                                marginBottom: 6,
                                                fontWeight: 600,
                                                color: antGrey.grey8,
                                            }}
                                        >
                                            {overviewTitle}
                                        </Text>
                                        <Text style={{ ...titleStyle, color: antGrey.grey6 }}>Overview</Text>
                                    </div>
                                </div>
                            }
                        </div>
                        <Link to={createUserSettingsRoute(UserSettingsPageView.Email)}>
                            <Button icon={<SendOutlined style={{ color: runwayColors.primary }} />}>
                                Manage subscriptions...
                            </Button>
                        </Link>
                    </div>
                </div>
                {isLoadingOrgDestinations ?
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        <Spin size="large" />
                        <Text style={{ color: runwayColors.primary, paddingTop: 10 }}>Loading...</Text>
                    </div>
                : canAccessTabularView ?
                    <div style={{ marginTop: 32 }}>
                        <OrgOverviewWorkflowsContent
                            orgId={orgId}
                            renderChartsView={renderChartsView}
                            onPageViewChange={handlePageViewTracking}
                        />
                    </div>
                :   renderChartsView()}
            </Content>
        </Layout>
    );
};

export default OrgOverviewV2;

interface OrgOverviewWorkflowsContentProps {
    orgId: string;
    renderChartsView: () => React.ReactNode;
    onPageViewChange: (pageView: string) => void;
}

const OrgOverviewWorkflowsContent: React.FC<OrgOverviewWorkflowsContentProps> = ({
    orgId,
    renderChartsView,
    onPageViewChange,
}) => {
    const summary = useSummaryView({ orgId });
    const {
        preferences,
        loading: { isLoadingInitialData: isLoadingOrgDestinations },
        availableVersions,
        availableDestinationIds,
        initializeWithDefaults,
        pagination: { destinationLimit },
    } = summary;

    const showCharts = preferences.pageView === PageViewType.Default;
    const isReleasesTabSelected = preferences.pageView === PageViewType.Tabular;

    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const user = useSelector((state: RootState) => selectUser(state));
    const appsInOrg = useSelector((state: RootState) => selectAllAppsIdsInOrg(state, orgId));
    const appGroupsInOrg = useSelector((state: RootState) => selectAllAppGroupNamesInOrg(state, orgId));
    const destinationsData = useSelector((state: RootState) => selectDestinationsData(state, orgId));

    const handleTrackFilterApply = React.useMemo(
        () => createFilterApplyTracker(org, user, appsInOrg, appGroupsInOrg, preferences, PageViewContext.OrgOverview),
        [org, user, appsInOrg, appGroupsInOrg, preferences],
    );

    React.useEffect(() => {
        const { pageView, tabularLayout } = preferences;
        const viewType = pageView === PageViewType.Default ? PageViewType.Default : `${pageView}-${tabularLayout}`;
        onPageViewChange(viewType);
    }, [onPageViewChange, preferences.pageView, preferences.tabularLayout]);

    const initialDestinationIds = React.useMemo(() => {
        if (availableDestinationIds.length === 0) return [];
        return destinationLimit ? availableDestinationIds.slice(0, destinationLimit) : availableDestinationIds;
    }, [availableDestinationIds, destinationLimit]);

    const defaultVersions = React.useMemo(() => {
        const validVersion = getValidVersionForDestinations(initialDestinationIds, destinationsData);
        if (validVersion) return [validVersion];
        return getDefaultVersionsForDestinations(availableVersions);
    }, [initialDestinationIds, destinationsData, availableVersions]);

    React.useEffect(() => {
        if (!isReleasesTabSelected) return;
        if (isLoadingOrgDestinations) return;
        if (!destinationLimit) return;
        if (initialDestinationIds.length === 0 || availableVersions.length === 0) return;

        initializeWithDefaults({
            versions: defaultVersions,
            destinationIds: initialDestinationIds,
        });
    }, [
        isLoadingOrgDestinations,
        destinationLimit,
        initialDestinationIds,
        availableVersions.length,
        defaultVersions,
        initializeWithDefaults,
        isReleasesTabSelected,
    ]);

    return (
        <>
            <OverviewTableHeader
                context={PageViewContext.OrgOverview}
                summary={summary}
                onTrackFilterApply={handleTrackFilterApply}
            />
            {showCharts ? renderChartsView() : <SummaryTable summary={summary} />}
        </>
    );
};
